import React, { useState, useEffect } from "react";
import { Dropdown, IDropdownOption, IDropdownStyles, Label, Stack } from "@fluentui/react";
import { getDgEnvironments, switchDg } from "../../api";
import { useUser } from "../../providers/UserProvider";
import { PermissionEnum } from "../../../../shared/frontend/enums/PermissionEnum";

interface DgEnvironmentSelectorProps {
  currentDg: string;
  onDgSwitch: (dg: string, displayName?: string) => void;
}

const dropdownStyles: Partial<IDropdownStyles> = {
  root: { minWidth: 150 },
};

const DgEnvironmentSelector: React.FC<DgEnvironmentSelectorProps> = ({
  currentDg,
  onDgSwitch,
}) => {
  const { hasPermission } = useUser();
  const canViewMultipleDg = hasPermission(PermissionEnum.VIEW_MULTIPLE_DG);
  const [options, setOptions] = useState<IDropdownOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!canViewMultipleDg) return;
    getDgEnvironments().then((data) => {
      setOptions(
        data.rows.map((env) => ({
          key: env.dg,
          text: env.display_name ?? env.name,
        }))
      );
    });
  }, [canViewMultipleDg]);

  const handleChange = async (
    _ev?: React.FormEvent<HTMLElement>,
    option?: IDropdownOption
  ) => {
    if (!option || option.key === currentDg || isLoading) return;
    setIsLoading(true);
    try {
      await switchDg(String(option.key));
      onDgSwitch(String(option.key), option.text);
    } catch (error) {
      console.error("Failed to switch DG:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!canViewMultipleDg || options.length === 0) return null;

  return (
    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
      <Label>DG Environment:</Label>
      <Dropdown
        placeholder="Select DG"
        selectedKey={currentDg}
        options={options}
        styles={dropdownStyles}
        onChange={handleChange}
        disabled={isLoading}
      />
    </Stack>
  );
};

export default DgEnvironmentSelector;
